/**
 * Pure helpers for the `useTabNavigationTree` hook. Extracted so the hook
 * file stays under the 200-line CLAUDE.md cap; these helpers are intentionally
 * React-agnostic and easy to unit-test.
 */

import {
  push,
  activate,
  dismiss,
  makeRowNode,
  makeTableNode,
  initTree,
  findById,
} from './breadcrumbTree';
import type {
  NavigationNode,
  NavigationNodeId,
  NavigationTree,
  FilterGroup,
  SortDefinition,
} from '../../../core/types/workspaceTypes';

export type RootInit = {
  readonly schema: string;
  readonly table: string;
  readonly filters: ReadonlyArray<FilterGroup>;
  readonly displayLabel?: string;
};

export type PushRowInput = {
  readonly schema: string;
  readonly table: string;
  readonly pk: ReadonlyArray<{ column: string; value: unknown }>;
  readonly displayLabel?: string;
};

export type PushTableInput = {
  readonly schema: string;
  readonly table: string;
  readonly filters: ReadonlyArray<FilterGroup>;
  readonly reverseFkColumn?: string;
  readonly displayLabel?: string;
  /**
   * Slice 5 — when true, the pushed table-node is suppressed from the
   * breadcrumb chip-row by default (the m2m join-table collapse). Defaults
   * to false. Callers that have already run `shouldCollapseJoinTable`
   * pass the result here.
   */
  readonly isJoinCollapsed?: boolean;
};

/** Build a fresh single-root tree from a root spec. */
export function buildRootTree(root: RootInit): NavigationTree {
  const rootNode = makeTableNode({
    schema: root.schema,
    table: root.table,
    filters: root.filters,
    displayLabel: root.displayLabel,
  });
  return initTree(rootNode);
}

/**
 * Look up an existing direct child of `base.activeNodeId` whose identity
 * matches the prospective push. Returns the matching node's id (so callers
 * can short-circuit `push` and instead just activate the existing child),
 * or `null` when there's no match.
 *
 * Identity:
 *  - row-node:   same (schema, table, pk values in order — column and value)
 *  - table-node: same (schema, table, reverseFkColumn). Filters are
 *    determined by the parent row in practice, so the reverse-FK column is
 *    used as the disambiguator (e.g. `User.customer_id` vs `User.sales_rep_id`).
 */
function findMatchingChild(
  base: NavigationTree,
  prospective:
    | { kind: 'row'; schema: string; table: string; pk: ReadonlyArray<{ column: string; value: unknown }> }
    | { kind: 'table'; schema: string; table: string; reverseFkColumn: string | undefined },
): NavigationNodeId | null {
  const parent = base.nodes[base.activeNodeId];
  if (!parent) return null;
  for (const childId of parent.childIds) {
    const child = base.nodes[childId];
    if (!child) continue;
    if (child.kind !== prospective.kind) continue;
    if (child.schema !== prospective.schema) continue;
    if (child.table !== prospective.table) continue;
    if (prospective.kind === 'row' && child.kind === 'row') {
      if (samePk(child.pk, prospective.pk)) return childId;
    } else if (prospective.kind === 'table' && child.kind === 'table') {
      if (child.reverseFkColumn === prospective.reverseFkColumn) return childId;
    }
  }
  return null;
}

function samePk(
  a: ReadonlyArray<{ column: string; value: unknown }>,
  b: ReadonlyArray<{ column: string; value: unknown }>,
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].column !== b[i].column) return false;
    if (a[i].value !== b[i].value) return false;
  }
  return true;
}

/**
 * Push a row-node onto `base`; returns the new tree and new node id. When a
 * direct child of the active node already matches (same schema+table+pk),
 * the existing child is activated and its id returned — no new branch is
 * created. Matches the user expectation that re-opening the same record
 * from the same parent reuses the existing breadcrumb entry.
 */
export function pushRowOnto(
  base: NavigationTree,
  input: PushRowInput,
): { tree: NavigationTree; newId: NavigationNodeId } {
  const existing = findMatchingChild(base, {
    kind: 'row',
    schema: input.schema,
    table: input.table,
    pk: input.pk,
  });
  if (existing) {
    return { tree: activate(base, existing), newId: existing };
  }
  const child = makeRowNode({
    schema: input.schema,
    table: input.table,
    pk: input.pk,
    displayLabel: input.displayLabel,
  });
  return push(base, child);
}

/**
 * Push a table-node onto `base`; returns the new tree and new node id. When
 * a direct child of the active node already matches (same schema+table+
 * reverseFkColumn), the existing child is activated and its id returned —
 * no new branch is created.
 */
export function pushTableOnto(
  base: NavigationTree,
  input: PushTableInput,
): { tree: NavigationTree; newId: NavigationNodeId } {
  const existing = findMatchingChild(base, {
    kind: 'table',
    schema: input.schema,
    table: input.table,
    reverseFkColumn: input.reverseFkColumn,
  });
  if (existing) {
    return { tree: activate(base, existing), newId: existing };
  }
  const child = makeTableNode({
    schema: input.schema,
    table: input.table,
    filters: input.filters,
    reverseFkColumn: input.reverseFkColumn,
    displayLabel: input.displayLabel,
    isJoinCollapsed: input.isJoinCollapsed,
  });
  return push(base, child);
}

/** Activate a node if it exists; returns null when no change should be made. */
export function activateIfPresent(
  tree: NavigationTree,
  id: NavigationNodeId,
): NavigationTree | null {
  if (!tree.nodes[id]) return null;
  try {
    findById(tree, id);
  } catch {
    return null;
  }
  return activate(tree, id);
}

/**
 * Dismiss a node + its subtree. Returns the next tree, or `null` if the entire
 * tree should be cleared (root dismissed).
 */
export function dismissNodeOnTree(
  tree: NavigationTree,
  id: NavigationNodeId,
): NavigationTree | null {
  return dismiss(tree, id);
}

/** Compute the currently-active node, if any. */
export function selectActiveNode(tree: NavigationTree | null): NavigationNode | null {
  if (!tree) return null;
  return tree.nodes[tree.activeNodeId] ?? null;
}

/** True when the tree is empty or only contains the root. */
export function isTreeEmptyOrRoot(tree: NavigationTree | null): boolean {
  return !tree || tree.rootId === tree.activeNodeId;
}

/**
 * Update the user-added filters on the currently-active TableNode. Returns
 * the new tree, or `null` if the active node isn't a TableNode (callers
 * should ignore the change in that case — the SRV has no filter state).
 */
export function setActiveUserFilters(
  tree: NavigationTree,
  userFilters: ReadonlyArray<FilterGroup>,
): NavigationTree | null {
  const active = tree.nodes[tree.activeNodeId];
  if (!active || active.kind !== 'table') return null;
  return {
    ...tree,
    nodes: {
      ...tree.nodes,
      [active.id]: { ...active, userFilters } as NavigationNode,
    },
  };
}

/** Update the user-added sorting on the currently-active TableNode. */
export function setActiveUserSorting(
  tree: NavigationTree,
  userSorting: ReadonlyArray<SortDefinition>,
): NavigationTree | null {
  const active = tree.nodes[tree.activeNodeId];
  if (!active || active.kind !== 'table') return null;
  return {
    ...tree,
    nodes: {
      ...tree.nodes,
      [active.id]: { ...active, userSorting } as NavigationNode,
    },
  };
}

// Per-node edit-mode helpers live in `./navTreeEditState`; re-exported here
// so the hook can keep one import surface.
export {
  setNodeEditMode,
  setNodePendingChanges,
  subtreeHasPendingChanges,
  getActiveTableEditState,
} from './navTreeEditState';
